import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge, Row, Col } from 'reactstrap';
import {
  IconMail,
  IconPhone,
  IconCalendarEvent,
  IconBan,
  IconCalendarTime,
  IconCheck,
  IconAlertTriangleFilled,
  IconFlagCheck,
} from '@tabler/icons-react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import {
  getAdvisoryLeadEndpoint,
  approveAdvisoryLeadEndpoint,
  rescheduleAdvisoryLeadEndpoint,
  cancelAdvisoryLeadEndpoint,
  finishAdvisoryLeadEndpoint,
} from '../services/dashboardService';
import { AdvisoryLead } from '../models/dashboardModels';
import {
  ADVISORY_BUSINESS_LINE_NAMES,
  ADVISORY_CONTACT_SCHEDULE_NAMES,
  ADVISORY_DELIVERY_TIME_NAMES,
  ADVISORY_GOAL_NAMES,
  ADVISORY_MONTHLY_BUDGET_NAMES,
  ADVISORY_PROPERTY_TYPE_NAMES,
  getAdvisoryErrorMessage,
  getAdvisoryStatusBadgeColor,
  getAdvisoryStatusLabel,
} from '../adapters/advisoryAdapters';
import ApproveAdvisoryLeadForm from './ApproveAdvisoryLeadForm';
import CancelAdvisoryLeadForm from './CancelAdvisoryLeadForm';
import FinishAdvisoryLeadForm from './FinishAdvisoryLeadForm';

interface Props {
  // `null` = modal cerrado. Deep-linkable: el padre lo abre pasando el id que
  // trae la URL (?leadId=...) o el de la fila en la que se clickeó "Ver
  // detalles" — este componente resuelve todo por su cuenta (no depende de
  // que el lead ya esté cargado en la lista del padre).
  leadId: string | null;
  onClose: () => void;
  // Avisa al padre que algo cambió (aprobado/reagendado/cancelado) para que
  // refresque su lista — el modal ya se refresca solo.
  onChanged: () => void;
  // El padre lo muestra en su toast (misma UI que usa para crear una
  // asesoría) — el modal se queda en el paso actual para que el admin pueda
  // corregir y reintentar en vez de perder lo ya elegido.
  onError: (message: string) => void;
}

// Un solo Modal con contenido dinámico por `step`, en vez de abrir un
// segundo Modal encima (modal-sobre-modal) para aprobar/reagendar/cancelar —
// evita sobrecargar la pantalla. Volver de un paso de acción regresa a
// 'detail' con el lead ya refrescado (se ve al toque el resultado: badge de
// estado + banner de fecha actualizados) en vez de cerrar todo.
type Step = 'detail' | 'approve' | 'reschedule' | 'cancel' | 'finish';

const STEP_TITLES: Record<Step, string> = {
  detail: 'Detalle de asesoría',
  approve: 'Aprobar asesoría',
  reschedule: 'Reagendar asesoría',
  cancel: 'Cancelar asesoría',
  finish: 'Finalizar asesoría',
};

const AdvisoryLeadDetailModal = ({ leadId, onClose, onChanged, onError }: Props) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { callEndpoint } = useCallApiAndLoad();

  const [lead, setLead] = useState<AdvisoryLead | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState<Step>('detail');

  const fetchLead = useCallback(async () => {
    if (!leadId) return;
    setIsLoading(true);
    setNotFound(false);
    const { status, data } = await callEndpoint(
      getAdvisoryLeadEndpoint(leadId, token),
    );
    setIsLoading(false);
    if (status !== 200) {
      setNotFound(true);
      return;
    }
    const response = fetchResponseAdapter(data);
    setLead(response.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  useEffect(() => {
    setStep('detail');
    if (leadId) {
      fetchLead();
    } else {
      setLead(null);
    }
  }, [leadId, fetchLead]);

  const handleApproveConfirm = async (
    assignedProperId: string,
    scheduledAt: string,
  ) => {
    if (!lead) return;
    const { status, data } = await callEndpoint(
      approveAdvisoryLeadEndpoint(
        lead.id,
        { assigned_proper_id: assignedProperId, scheduled_at: scheduledAt },
        token,
      ),
    );
    if (status !== 200 && status !== 201) {
      onError(getAdvisoryErrorMessage(data, 'No se pudo aprobar la asesoría.'));
      return;
    }
    setStep('detail');
    await fetchLead();
    onChanged();
  };

  const handleRescheduleConfirm = async (
    assignedProperId: string,
    scheduledAt: string,
  ) => {
    if (!lead) return;
    const { status, data } = await callEndpoint(
      rescheduleAdvisoryLeadEndpoint(
        lead.id,
        { assigned_proper_id: assignedProperId, scheduled_at: scheduledAt },
        token,
      ),
    );
    if (status !== 200 && status !== 201) {
      onError(getAdvisoryErrorMessage(data, 'No se pudo reagendar la asesoría.'));
      return;
    }
    setStep('detail');
    await fetchLead();
    onChanged();
  };

  const handleCancelConfirm = async (cancellationReason: string) => {
    if (!lead) return;
    const { status, data } = await callEndpoint(
      cancelAdvisoryLeadEndpoint(lead.id, cancellationReason, token),
    );
    if (status !== 200 && status !== 201) {
      onError(getAdvisoryErrorMessage(data, 'No se pudo cancelar la asesoría.'));
      return;
    }
    setStep('detail');
    await fetchLead();
    onChanged();
  };

  const handleFinishConfirm = async (notes?: string) => {
    if (!lead) return;
    const { status, data } = await callEndpoint(
      finishAdvisoryLeadEndpoint(lead.id, notes, token),
    );
    if (status !== 200 && status !== 201) {
      onError(getAdvisoryErrorMessage(data, 'No se pudo finalizar la asesoría.'));
      return;
    }
    setStep('detail');
    await fetchLead();
    onChanged();
  };

  const canCancel =
    lead &&
    (lead.status === 'pending_approval' ||
      lead.status === 'scheduled' ||
      lead.status === 'rescheduled');
  const canReschedule =
    lead && (lead.status === 'scheduled' || lead.status === 'rescheduled');
  const canApprove = lead && lead.status === 'pending_approval';
  // Sólo se puede finalizar una asesoría que efectivamente se agendó (ver
  // AdminAdvisoryService.finish en user-service).
  const canFinish =
    lead && (lead.status === 'scheduled' || lead.status === 'rescheduled');

  return (
    <Modal
      isOpen={!!leadId}
      toggle={onClose}
      className="modal-dialog-centered"
      size={step === 'detail' ? 'lg' : undefined}
    >
      <ModalHeader toggle={onClose}>{STEP_TITLES[step]}</ModalHeader>

      {step === 'detail' && (
        <>
          <ModalBody>
            {isLoading && <p className="text-muted">Cargando...</p>}
            {notFound && (
              <p className="text-danger">No se encontró la asesoría.</p>
            )}
            {lead && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <h6 className="text-muted mb-1">Contacto</h6>
                    <div className="font-weight-bold">
                      {lead.customerProfile.first_name}{' '}
                      {lead.customerProfile.last_name}
                    </div>
                    <div className="d-flex align-items-center text-muted">
                      <IconMail size={12} className="me-1" />
                      {lead.customerProfile.email}
                    </div>
                    <div className="d-flex align-items-center text-muted">
                      <IconPhone size={12} className="me-1" />
                      {lead.customerProfile.area_code || '+503'}{' '}
                      {lead.customerProfile.phone}
                    </div>
                    <Badge
                      className="mt-1"
                      color={lead.is_customer_verified ? 'success' : 'warning'}
                    >
                      {lead.is_customer_verified ? 'Verificado' : 'No verificado'}
                    </Badge>
                  </Col>
                  <Col md={6}>
                    <h6 className="text-muted mb-1">Estado</h6>
                    <Badge
                      className="badge-lg"
                      color={getAdvisoryStatusBadgeColor(lead.status)}
                    >
                      {getAdvisoryStatusLabel(lead.status)}
                    </Badge>
                    {lead.cancellation_reason && (
                      <div className="text-muted small mt-1">
                        Motivo: {lead.cancellation_reason}
                      </div>
                    )}
                    {lead.finish_notes && (
                      <div className="text-muted small mt-1">
                        Notas: {lead.finish_notes}
                      </div>
                    )}
                  </Col>
                </Row>

                {lead.scheduled_at && (
                  // Resultado de todo el proceso (crear/aprobar/reagendar), no
                  // un dato más de logística — banner propio arriba de todo,
                  // junto al bloque de Contacto/Estado, para que se vea de un
                  // vistazo sin bajar el resto del modal.
                  <div
                    className="d-flex align-items-center rounded mb-3 p-3"
                    style={{ backgroundColor: '#eef6ff', border: '1px solid #b8d9ff' }}
                  >
                    <IconCalendarEvent size={32} className="text-primary me-3" />
                    <div>
                      <div className="text-uppercase text-muted small">
                        Cita agendada
                      </div>
                      <div className="font-weight-bold" style={{ fontSize: '20px' }}>
                        {new Date(lead.scheduled_at).toLocaleString('es', {
                          dateStyle: 'long',
                          timeStyle: 'short',
                        })}
                      </div>
                    </div>
                  </div>
                )}

                <hr className="my-4" />

                <h6 className="text-uppercase text-muted small mb-3">
                  Información de interés
                </h6>
                <Row className="mb-2">
                  <Col md={4}>
                    <h6 className="text-muted mb-1">Línea</h6>
                    <div>
                      <span className="font-weight-bold">
                        {ADVISORY_BUSINESS_LINE_NAMES[lead.delivery_time] ??
                          lead.delivery_time}
                      </span>{' '}
                      (
                      {ADVISORY_DELIVERY_TIME_NAMES[lead.delivery_time] ??
                        lead.delivery_time}
                      )
                    </div>
                  </Col>
                  <Col md={4}>
                    <h6 className="text-muted mb-1">Interés</h6>
                    <div>{ADVISORY_GOAL_NAMES[lead.goal] ?? lead.goal}</div>
                  </Col>
                  <Col md={4}>
                    <h6 className="text-muted mb-1">Tipo de propiedad</h6>
                    <div>
                      {ADVISORY_PROPERTY_TYPE_NAMES[lead.property_type] ??
                        lead.property_type}
                    </div>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <h6 className="text-muted mb-1">Ubicación</h6>
                    <div>{lead.zone || "Sin zona especificada"}</div>
                  </Col>
                  <Col md={4}>
                    <h6 className="text-muted mb-1">Presupuesto mensual</h6>
                    <div>
                      {ADVISORY_MONTHLY_BUDGET_NAMES[lead.monthly_budget] ??
                        lead.monthly_budget}
                    </div>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="text-uppercase text-muted small mb-3">
                  Logística
                </h6>
                <Row className="mb-2">
                  <Col md={4}>
                    <h6 className="text-muted mb-1">Horario de contacto</h6>
                    <div>
                      {ADVISORY_CONTACT_SCHEDULE_NAMES[lead.contact_schedule] ??
                        lead.contact_schedule}
                    </div>
                  </Col>
                  <Col md={4}>
                    <h6 className="text-muted mb-1">SDR asignado</h6>
                    {lead.assignedSdr ? (
                      <div className="font-weight-bold">
                        {lead.assignedSdr.first_name} {lead.assignedSdr.last_name}
                      </div>
                    ) : (
                      <div className="d-flex align-items-center font-weight-bold text-warning">
                        <IconAlertTriangleFilled size={14} className="me-1" />
                        Sin asignar
                      </div>
                    )}
                  </Col>
                  <Col md={4}>
                    <h6 className="text-muted mb-1">Proper asignado</h6>
                    {lead.assignedProper ? (
                      <div className="font-weight-bold">
                        {lead.assignedProper.first_name}{' '}
                        {lead.assignedProper.last_name}
                      </div>
                    ) : (
                      <div className="d-flex align-items-center font-weight-bold text-warning">
                        <IconAlertTriangleFilled size={14} className="me-1" />
                        Sin asignar
                      </div>
                    )}
                  </Col>
                </Row>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={onClose}>
              Cerrar
            </Button>
            {canCancel && (
              <Button color="outline-danger" onClick={() => setStep('cancel')}>
                <IconBan size={16} className="me-1" />
                Cancelar
              </Button>
            )}
            {canApprove && (
              <Button
                color="success"
                className="px-4"
                onClick={() => setStep('approve')}
              >
                <IconCheck size={16} className="me-1" />
                Aprobar
              </Button>
            )}
            {canReschedule && (
              <Button
                color="warning"
                className="px-4"
                onClick={() => setStep('reschedule')}
              >
                <IconCalendarTime size={16} className="me-1" />
                Reagendar
              </Button>
            )}
            {canFinish && (
              <Button
                color="success"
                className="px-4"
                onClick={() => setStep('finish')}
              >
                <IconFlagCheck size={16} className="me-1" />
                Finalizar
              </Button>
            )}
          </ModalFooter>
        </>
      )}

      {(step === 'approve' || step === 'reschedule') && (
        <ApproveAdvisoryLeadForm
          onCancel={() => setStep('detail')}
          onConfirm={step === 'approve' ? handleApproveConfirm : handleRescheduleConfirm}
          confirmLabel={step === 'approve' ? 'Aprobar y agendar' : 'Reagendar'}
          confirmColor={step === 'approve' ? 'success' : 'warning'}
          initialScheduledAt={step === 'reschedule' ? lead?.scheduled_at ?? null : null}
        />
      )}

      {step === 'cancel' && (
        <CancelAdvisoryLeadForm
          onCancel={() => setStep('detail')}
          onConfirm={handleCancelConfirm}
        />
      )}

      {step === 'finish' && (
        <FinishAdvisoryLeadForm
          onCancel={() => setStep('detail')}
          onConfirm={handleFinishConfirm}
        />
      )}
    </Modal>
  );
};

export default AdvisoryLeadDetailModal;
